import {
  BuildingIcon,
  CreditCardIcon,
  FileTextIcon,
  MapPinIcon,
  SearchIcon,
  UserIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  compactProperties,
  type EntityCandidate,
  entityCoords,
  type FindEntityByNameArgs,
  type FindEntityByNameResult,
  primaryLabel,
  visibleLabels,
} from '@/features/agent/tools/find-entity-by-name'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { cn } from '@/utils/cn'
import type { ToolActionHandlers, ToolActionKind } from './_shared/chat-tool-actions'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'
import { TOOL_ACTION_ICONS, TOOL_ACTION_LABEL_KEYS } from './_shared/ToolCardActions'

const LABEL_ICON: Record<string, typeof UserIcon> = {
  Person: UserIcon,
  Company: BuildingIcon,
  BankAccount: CreditCardIcon,
  Address: MapPinIcon,
  Document: FileTextIcon,
}

/**
 * The graph actions offered for one candidate, already resolved against the
 * current object set by the connected tool. Per-row rather than per-card
 * because each candidate has its own answer to "is this already on canvas?".
 */
export interface CandidateGraphActions {
  primary: ToolActionKind
  secondary: ToolActionKind[]
  handlers: ToolActionHandlers
}

export interface FindEntityByNameViewProps {
  args: FindEntityByNameArgs
  parsed: FindEntityByNameResult | null
  running: boolean
  errored: boolean
  /** Resolve the graph actions for one candidate. Returns null when there is
   *  no case in scope and the row should offer none. Supplied by the
   *  connected tool, which owns the object-set lookup. */
  graphActionsFor: (candidate: EntityCandidate) => CandidateGraphActions | null
  /** Open a single geolocated candidate on the map. Supplied by the connected
   *  tool. Only invoked for candidates that resolve to coordinates. */
  onShowOnMap: (candidate: EntityCandidate, coords: { lat: number; lon: number }) => void
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

/** Pure presentation for a `find_entity_by_name` tool-result card. */
export function FindEntityByNameView({
  args,
  parsed,
  running,
  errored,
  graphActionsFor,
  onShowOnMap,
  defaultOpen = false,
}: FindEntityByNameViewProps) {
  const { t } = useTranslation()

  const count = parsed?.count ?? parsed?.candidates?.length ?? 0
  const label = running
    ? t('chat.findEntity.searching')
    : errored
      ? t('chat.findEntity.failed')
      : count === 0
        ? t('chat.findEntity.noMatches')
        : t('chat.findEntity.matches', { count })

  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'
  const description = (
    <>
      {label}{' '}
      <code className="ml-0.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
        "{args.query}"
      </code>
    </>
  )

  const hasBody = !running && (errored || (parsed?.candidates && parsed.candidates.length > 0))
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={SearchIcon}
        toolName="find_entity_by_name"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />

      {open && (
        <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
          {parsed?.status === 'error' && (
            <p className="whitespace-pre-wrap text-xs text-muted-foreground">
              {parsed.error ?? 'Unknown error'}
            </p>
          )}
          {parsed?.candidates && parsed.candidates.length > 0 && (
            <ul className="flex flex-col gap-1">
              {parsed.candidates.map(c => (
                <CandidateRow
                  key={c.element_id}
                  candidate={c}
                  graphActions={graphActionsFor(c)}
                  onShowOnMap={onShowOnMap}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}

interface CandidateRowProps {
  candidate: EntityCandidate
  graphActions: CandidateGraphActions | null
  onShowOnMap: (candidate: EntityCandidate, coords: { lat: number; lon: number }) => void
}

/** Shared shape for the row's icon-only actions. */
const ROW_BUTTON_CLASS = cn(
  'flex shrink-0 items-center justify-center rounded-md border border-transparent px-2.5 text-muted-foreground transition-colors md:px-2',
  'hover:border-border hover:bg-muted/60 hover:text-foreground'
)

function CandidateRow({ candidate: c, graphActions, onShowOnMap }: CandidateRowProps) {
  const { t } = useTranslation()
  const label = primaryLabel(c.labels)
  const Icon = (label && LABEL_ICON[label]) || UserIcon
  const shownLabels = visibleLabels(c.labels)
  const props = compactProperties(c.properties)
  const coords = entityCoords(c.properties)

  return (
    <li className="flex items-stretch gap-1">
      <div className="flex min-w-0 flex-1 items-start gap-2 px-2 py-1.5 text-left text-xs">
        <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-foreground">{c.name}</span>
            {shownLabels.map(l => (
              <span
                key={l}
                className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
              >
                {l}
              </span>
            ))}
          </div>
          {props.length > 0 && (
            <div className="text-[10px] text-muted-foreground">{props.join(' · ')}</div>
          )}
          <div className="truncate font-mono text-[10px] text-muted-foreground/70">
            {c.element_id}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-stretch gap-1">
        {graphActionKinds(graphActions).map(kind => {
          const ActionIcon = TOOL_ACTION_ICONS[kind]
          const actionLabel = t(TOOL_ACTION_LABEL_KEYS[kind])
          return (
            <button
              key={kind}
              type="button"
              onClick={graphActions?.handlers[kind]}
              className={ROW_BUTTON_CLASS}
              title={actionLabel}
              aria-label={`${actionLabel}: ${c.name}`}
            >
              <ActionIcon className="size-3.5" />
            </button>
          )
        })}
        {coords && (
          <button
            type="button"
            onClick={() => onShowOnMap(c, coords)}
            className={ROW_BUTTON_CLASS}
            title={t('chat.toolActions.showOnMap')}
            aria-label={`${t('chat.toolActions.showOnMap')}: ${c.name}`}
          >
            <MapPinIcon className="size-3.5" />
          </button>
        )}
      </div>
    </li>
  )
}

/**
 * Which graph actions a row paints: the resolved primary plus at most one
 * secondary, and only those the tool can actually execute. Mirrors the
 * two-button ceiling `ToolCardActions` keeps for card footers — a row is
 * even tighter on space than a footer.
 */
function graphActionKinds(actions: CandidateGraphActions | null): ToolActionKind[] {
  if (!actions) return []
  const secondary = actions.secondary[0]
  const kinds = secondary ? [actions.primary, secondary] : [actions.primary]
  return kinds.filter(k => actions.handlers[k])
}
